/**
 * Keskitetyt muotoilu- ja apufunktiot koko sovellukselle
 * 
 * Käyttö:
 * import { getCustomerDisplayName, formatCurrency, formatDate } from "@/lib/formatters";
 */

import { format, formatDistanceToNow, parseISO, isToday, isTomorrow, isPast, differenceInDays } from "date-fns";
import { fi } from "date-fns/locale";

// ============================================================================
// KUORMIEN PÄIVÄMÄÄRÄT
// ============================================================================

/**
 * Hae kuorman päivämäärälle "ystävällinen" nimi
 * Käytetään kuormien ja tehtävien päivämäärien näyttämiseen
 * 
 * @example
 * getLoadDateLabel("2024-01-20") // "Tänään" (jos tänään on 20.1.)
 * getLoadDateLabel("2024-01-21") // "Huomenna"
 * getLoadDateLabel("2024-01-18") // "perjantai 18.1." (mennyt päivä)
 * getLoadDateLabel("2024-01-25") // "lauantai 25.1." (tuleva päivä)
 */
export function getLoadDateLabel(dateStr: string): string {
  const date = new Date(dateStr);
  if (isToday(date)) return "Tänään";
  if (isTomorrow(date)) return "Huomenna";
  if (isPast(date)) return format(date, "EEEE d.M.", { locale: fi });
  return format(date, "EEEE d.M.", { locale: fi });
}

// ============================================================================
// ASIAKKAIDEN NIMET
// ============================================================================

interface CustomerLike {
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  contact_person?: string | null;
}

/**
 * Hae asiakkaan näyttönimi
 * Toimii kaikilla asiakasobjekteilla joilla on first_name, last_name tai company_name
 * 
 * @example
 * getCustomerDisplayName({ first_name: "Matti", last_name: "Meikäläinen" }) // "Matti Meikäläinen"
 * getCustomerDisplayName({ company_name: "Yritys Oy" }) // "Yritys Oy"
 * getCustomerDisplayName(null) // "Tuntematon"
 */
export function getCustomerDisplayName(customer: CustomerLike | null | undefined): string {
  if (!customer) return "Tuntematon";
  
  if (customer.company_name) {
    return customer.company_name;
  }
  
  const fullName = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
  return fullName || "Tuntematon";
}

/**
 * Lyhyt versio asiakkaan nimestä (sukunimi, etunimi)
 */
export function getCustomerShortName(customer: CustomerLike | null | undefined): string {
  if (!customer) return "Tuntematon";
  
  if (customer.company_name) {
    return customer.company_name;
  }
  
  if (customer.last_name && customer.first_name) {
    return `${customer.last_name}, ${customer.first_name}`;
  }
  
  return customer.last_name || customer.first_name || "Tuntematon";
}

// ============================================================================
// VALUUTAT JA HINNAT
// ============================================================================

/**
 * Muotoile euromäärä suomalaiseen muotoon
 * 
 * @example
 * formatCurrency(1234.56) // "1 234,56 €"
 * formatCurrency(0) // "0,00 €"
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return "0,00 €";
  
  return new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

/**
 * Muotoile numero ilman valuuttasymbolia
 * 
 * @example
 * formatNumber(1234.56) // "1 234,56"
 */
export function formatNumber(value: number | null | undefined, decimals = 2): string {
  if (value == null) return "0";
  
  return new Intl.NumberFormat("fi-FI", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Muotoile prosentti
 * 
 * @example
 * formatPercent(25.5) // "25,5 %"
 */
export function formatPercent(value: number | null | undefined): string {
  if (value == null) return "0 %";
  
  return `${formatNumber(value, 1)} %`;
}

// ============================================================================
// PÄIVÄMÄÄRÄT JA AJAT
// ============================================================================

/**
 * Muotoile päivämäärä suomalaiseen muotoon
 * 
 * @example
 * formatDate("2024-01-15") // "15.1.2024"
 * formatDate(new Date()) // "20.1.2026"
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "-";
  
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "d.M.yyyy", { locale: fi });
  } catch {
    return "-";
  }
}

/**
 * Muotoile päivämäärä ja kellonaika
 * 
 * @example
 * formatDateTime("2024-01-15T14:30:00") // "15.1.2024 14:30"
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return "-";
  
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "d.M.yyyy HH:mm", { locale: fi });
  } catch {
    return "-";
  }
}

/**
 * Muotoile päivämäärä pitkässä muodossa
 * 
 * @example
 * formatDateLong("2024-01-15") // "maanantai 15. tammikuuta 2024"
 */
export function formatDateLong(date: string | Date | null | undefined): string {
  if (!date) return "-";
  
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return format(d, "EEEE d. MMMM yyyy", { locale: fi });
  } catch {
    return "-";
  }
}

/**
 * Muotoile suhteellinen aika ("3 päivää sitten")
 * 
 * @example
 * formatRelativeTime("2024-01-12") // "3 päivää sitten"
 */
export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return "-";
  
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return formatDistanceToNow(d, { addSuffix: true, locale: fi });
  } catch {
    return "-";
  }
}

/**
 * Hae päivämäärän "ystävällinen" nimi (Tänään, Huomenna, viikonpäivä, tai pvm)
 * 
 * @example
 * getDateLabel("2024-01-20") // "Tänään" (jos tänään on 20.1.)
 * getDateLabel("2024-01-21") // "Huomenna"
 * getDateLabel("2024-01-22") // "tiistai" (jos alle viikko)
 * getDateLabel("2024-02-15") // "15.2.2024"
 */
export function getDateLabel(date: string | Date | null | undefined): string {
  if (!date) return "-";
  
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    
    if (isToday(d)) return "Tänään";
    if (isTomorrow(d)) return "Huomenna";
    
    const daysAway = differenceInDays(d, new Date());
    if (daysAway > 0 && daysAway < 7) {
      return format(d, "EEEE", { locale: fi });
    }
    
    return format(d, "d.M.yyyy", { locale: fi });
  } catch {
    return "-";
  }
}

// ============================================================================
// OSOITTEET
// ============================================================================

interface AddressLike {
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
}

/**
 * Muotoile osoite yhdelle riville
 * 
 * @example
 * formatAddress({ address: "Katu 1", postal_code: "00100", city: "Helsinki" })
 * // "Katu 1, 00100 Helsinki"
 */
export function formatAddress(data: AddressLike | null | undefined): string {
  if (!data) return "-";
  
  const parts: string[] = [];
  
  if (data.address) {
    parts.push(data.address);
  }
  
  const postalCity = [data.postal_code, data.city].filter(Boolean).join(" ");
  if (postalCity) {
    parts.push(postalCity);
  }
  
  return parts.length > 0 ? parts.join(", ") : "-";
}

/**
 * Muotoile osoite useammalle riville
 */
export function formatAddressMultiline(data: AddressLike | null | undefined): string[] {
  if (!data) return [];
  
  const lines: string[] = [];
  
  if (data.address) {
    lines.push(data.address);
  }
  
  const postalCity = [data.postal_code, data.city].filter(Boolean).join(" ");
  if (postalCity) {
    lines.push(postalCity);
  }
  
  return lines;
}

// ============================================================================
// PUHELINNUMEROT
// ============================================================================

/**
 * Muotoile puhelinnumero
 * 
 * @example
 * formatPhoneNumber("0401234567") // "040 123 4567"
 * formatPhoneNumber("+358401234567") // "+358 40 123 4567"
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "-";
  
  // Poista välilyönnit ja viivat
  const cleaned = phone.replace(/[\s-]/g, "");
  
  // Suomalainen numero ilman maakoodia
  if (cleaned.match(/^0\d{9}$/)) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  
  // Suomalainen numero maakoodilla
  if (cleaned.match(/^\+358\d{9}$/)) {
    return `+358 ${cleaned.slice(4, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
  }
  
  // Palauta alkuperäinen jos ei tunnisteta
  return phone;
}

// ============================================================================
// MITAT JA DIMENSIOT
// ============================================================================

/**
 * Muotoile dimensiot (leveys x korkeus x paksuus)
 * Arvot metreinä, näytetään millimetreinä
 * 
 * @example
 * formatDimensions(0.6, 0.8, 0.08) // "600 × 800 × 80 mm"
 */
export function formatDimensions(
  width: number | null | undefined,
  height: number | null | undefined,
  thickness: number | null | undefined
): string {
  const parts = [width, height, thickness]
    .filter((v) => v != null)
    .map((v) => Math.round(v! * 1000));
  
  if (parts.length === 0) return "-";
  
  return `${parts.join(" × ")} mm`;
}

/**
 * Muotoile yksittäinen mitta (metri -> millimetri)
 */
export function formatDimensionMm(value: number | null | undefined): string {
  if (value == null) return "-";
  return `${Math.round(value * 1000)} mm`;
}

/**
 * Muotoile pinta-ala (neliömetri)
 */
export function formatArea(value: number | null | undefined): string {
  if (value == null) return "-";
  return `${formatNumber(value, 2)} m²`;
}

// ============================================================================
// TILAUSNUMEROT JA TUNNISTEET
// ============================================================================

/**
 * Muotoile tilausnumero lyhyempään muotoon
 * 
 * @example
 * formatOrderNumber("TIL-2024-00001") // "TIL-2024-00001" (ei muutosta)
 * formatOrderNumber("TIL-2024-00001", true) // "00001"
 */
export function formatOrderNumber(orderNumber: string | null | undefined, shortForm = false): string {
  if (!orderNumber) return "-";
  
  if (shortForm) {
    const parts = orderNumber.split("-");
    return parts[parts.length - 1] || orderNumber;
  }
  
  return orderNumber;
}

// ============================================================================
// APUFUNKTIOT
// ============================================================================

/**
 * Yhdistä CSS-luokat (kuten clsx/cn, mutta yksinkertaisempi)
 */
export function classNames(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

/**
 * Lyhennä teksti tiettyyn pituuteen
 * 
 * @example
 * truncate("Pitkä teksti tässä", 10) // "Pitkä t..."
 */
export function truncate(text: string | null | undefined, maxLength: number): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Tarkista onko arvo tyhjä (null, undefined, tyhjä string, tyhjä array)
 */
export function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}
